import type { ComponentPropsWithRef, ReactNode } from 'react';

import { useIntl } from '~/hooks/useIntl';
import { clsx } from '~/styles/utils';

import type { IllustrationName } from '../Illustration/Illustration';

import { Card } from '../Card/Card';
import { Illustration } from '../Illustration/Illustration';
import { Infotip } from '../Infotip/Infotip';
import {
  contentStyle,
  headerStyle,
  iconStyle,
  illustrationStyle,
  illustrationWrapperStyle,
  valueStyle,
  wrapperStyle,
} from './StatBlock.css';

type Props = Omit<ComponentPropsWithRef<typeof Card.Root>, 'children' | 'render' | 'size'> & {
  illustration: IllustrationName;
  illustrationStrokeWidth?: number;
  label: string;
  tooltip?: ReactNode;
  value: number;
};

export const StatBlock = ({
  illustration,
  illustrationStrokeWidth = 1.5,
  label,
  tooltip,
  value,
  variant = 'secondary-brand',
  ...delegated
}: Props) => {
  const { formatCurrency } = useIntl();

  return (
    <Card.Root {...delegated} className={clsx(wrapperStyle, delegated.className)} variant={variant}>
      <div className={illustrationWrapperStyle}>
        <Illustration className={illustrationStyle} name={illustration} strokeWidth={illustrationStrokeWidth} />
      </div>

      <div className={contentStyle}>
        <dt className={headerStyle}>{label}</dt>

        <dd className={valueStyle}>{formatCurrency(value)}</dd>
      </div>

      {tooltip !== undefined && (
        <span className={iconStyle}>
          <Infotip>{tooltip}</Infotip>
        </span>
      )}
    </Card.Root>
  );
};
